import { prefersReducedMotion } from './dom.js';

const SELECTOR_ROOT = '[data-bp-selector]';
const OPTION_SELECTOR = '[data-bp-option]';
const READY_ATTRIBUTE = 'data-bp-hero-selector-ready';
let generatedElementId = 0;

function trimmed(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function readTextData(option, attribute, fallbackSelector) {
  const direct = trimmed(option.getAttribute(attribute));
  if (direct) return direct;
  return trimmed(option.querySelector(fallbackSelector)?.textContent);
}

function headingTextFromChildNodes(element) {
  const nodes = element?.childNodes;
  if (!nodes || nodes.length === 0) return null;

  let text = '';
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.nodeType === 3) {
      text += node.textContent ?? '';
      continue;
    }
    if (node.nodeType !== 1) continue;

    const nodeName = (node.tagName || node.nodeName || '').toUpperCase();
    if (nodeName === 'BR') {
      text += '\n';
      continue;
    }
    const nestedText = headingTextFromChildNodes(node);
    text += nestedText ?? node.textContent ?? '';
  }
  return text;
}

function readHeadingElement(element) {
  const decodedText = headingTextFromChildNodes(element);
  if (decodedText !== null) return trimmed(decodedText).replace(/\r\n?|\n/g, '<br>');
  return trimmed(element?.innerHTML) || trimmed(element?.textContent);
}

function readHeadingData(option) {
  const direct = trimmed(option.getAttribute('data-bp-feature-heading'));
  if (direct) return direct;
  return readHeadingElement(option.querySelector('[data-bp-feature-heading-data]'));
}

function readImageSource(element) {
  return trimmed(element?.currentSrc)
    || trimmed(element?.src)
    || trimmed(element?.getAttribute?.('src'))
    || trimmed(element?.textContent);
}

function readHeroImageData(option, slug, heroImageOverrides) {
  const direct = trimmed(option.getAttribute('data-bp-hero-image'));
  if (direct) return direct;

  const authoredOverride = heroImageOverrides?.get?.(slug);
  if (authoredOverride) return authoredOverride;
  return readImageSource(option.querySelector('[data-bp-hero-image-data]'));
}

function interpolateMarketTemplate(template, market, slug) {
  const authoredTemplate = trimmed(template);
  if (
    !authoredTemplate
    || (authoredTemplate.includes('{{market}}') && !market)
    || (authoredTemplate.includes('{{slug}}') && !slug)
  ) return '';

  const interpolated = authoredTemplate.replace(
    /\{\{(market|slug)\}\}/g,
    (_, token) => (token === 'market' ? market : slug),
  );
  return /\{\{[^{}]*\}\}/.test(interpolated) ? '' : interpolated;
}

function collectHeroImageOverrides(root) {
  const overrides = new Map();
  const rootSelector = '[data-bp-page="home"][data-bp-hero-runtime="v2"]';
  for (const node of root.querySelectorAll(
    '[data-bp-hero-image-override][data-bp-slug]',
  )) {
    const owner = node.closest?.(rootSelector);
    if (owner && owner !== root) continue;
    const slug = trimmed(node.getAttribute('data-bp-slug'));
    const image = readImageSource(node);
    if (slug && image && !overrides.has(slug)) overrides.set(slug, image);
  }
  return overrides;
}

function optionLabel(option) {
  const label = option.querySelector('.bp-hsel-option-label');
  return trimmed(label?.textContent ?? option.textContent);
}

export function readHeroOptionData(option, templates = {}) {
  const authoredTemplates = templates ?? {};
  const directValue = trimmed(option.getAttribute('data-value'));
  const slug = trimmed(option.querySelector('[data-bp-slug]')?.textContent);
  const label = optionLabel(option);
  const featureHeading = readHeadingData(option);
  const featureCtaLabel = readTextData(
    option,
    'data-bp-feature-cta-label',
    '[data-bp-feature-cta-label-data]',
  );
  const marketUrl = readTextData(option, 'data-bp-market-url', '[data-bp-market-url-data]');

  return {
    value: directValue || slug,
    label,
    heroImage: readHeroImageData(option, slug, authoredTemplates.heroImageOverrides),
    featureHeading: featureHeading || interpolateMarketTemplate(
      authoredTemplates.featureHeading,
      label,
      slug,
    ),
    featureCtaLabel: featureCtaLabel || interpolateMarketTemplate(
      authoredTemplates.featureCtaLabel,
      label,
      slug,
    ),
    marketUrl: marketUrl || interpolateMarketTemplate(
      authoredTemplates.marketUrl,
      label,
      slug,
    ),
  };
}

function escapeHeadingSegment(segment) {
  return segment
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function safeHeadingHtml(authoredHeading) {
  const segments = authoredHeading.split(/<br\s*\/?\s*>|\r\n?|\n/gi);
  let html = escapeHeadingSegment(segments[0] ?? '');
  for (let index = 1; index < segments.length; index += 1) {
    // A break suppressed at wide widths must still carry a separator or the segments fuse; a trailing space collapses when the break renders.
    if (html && !/\s$/.test(html)) html += ' ';
    html += `<br>${escapeHeadingSegment(segments[index])}`;
  }
  return html;
}

function validatedMarketUrl(authoredUrl, environment, documentObject, windowObject) {
  if (!authoredUrl) return '';

  const URLConstructor = environment.URL ?? windowObject.URL ?? globalThis.URL;
  if (typeof URLConstructor !== 'function') return '';

  const baseUrl = trimmed(documentObject?.baseURI)
    || trimmed(environment.location?.href)
    || trimmed(windowObject.location?.href)
    || 'https://relative.invalid/';
  try {
    const parsed = new URLConstructor(authoredUrl, baseUrl);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? authoredUrl : '';
  } catch {
    return '';
  }
}

function cssBackground(imageUrl) {
  const escapedUrl = imageUrl
    .replaceAll('\\', '\\\\')
    .replaceAll('"', '\\"')
    .replaceAll('\n', '\\a ')
    .replaceAll('\r', '');
  return `url("${escapedUrl}")`;
}

function createCrossfade(hero, environment) {
  if (!hero) return () => {};

  const windowObject = environment.window ?? globalThis;
  const getStyle = environment.getComputedStyle?.bind(environment)
    ?? windowObject.getComputedStyle?.bind(windowObject);
  const requestFrame = environment.requestAnimationFrame?.bind(environment)
    ?? windowObject.requestAnimationFrame?.bind(windowObject)
    ?? ((callback) => callback());
  const delay = environment.setTimeout?.bind(environment)
    ?? windowObject.setTimeout?.bind(windowObject)
    ?? ((callback) => callback());
  const cancelDelay = environment.clearTimeout?.bind(environment)
    ?? windowObject.clearTimeout?.bind(windowObject)
    ?? (() => {});
  const reducedMotion = prefersReducedMotion(environment.matchMedia ? environment : windowObject);
  const computedBackground = getStyle?.(hero)?.backgroundImage;
  const authoredBackground = hero.style.backgroundImage || computedBackground;
  const defaultBackground = authoredBackground && authoredBackground !== 'none'
    ? authoredBackground
    : '';
  if (defaultBackground) hero.style.setProperty('--bp-hero-cur', defaultBackground);

  let settleTimer = null;
  let renderVersion = 0;

  return (imageUrl) => {
    const background = imageUrl ? cssBackground(imageUrl) : defaultBackground;
    renderVersion += 1;
    const version = renderVersion;
    const replacingActiveFade = settleTimer !== null
      || hero.classList.contains('bp-hero-fading');

    if (replacingActiveFade) {
      const previousNext = hero.style.getPropertyValue('--bp-hero-next');
      if (previousNext) hero.style.setProperty('--bp-hero-cur', previousNext);
    }
    if (settleTimer !== null) {
      cancelDelay(settleTimer);
      settleTimer = null;
    }
    hero.classList.remove('bp-hero-fading');
    hero.style.backgroundImage = background;

    if (reducedMotion) {
      hero.style.setProperty('--bp-hero-cur', background);
      return;
    }

    hero.style.setProperty('--bp-hero-next', background);
    const startFade = () => {
      if (version !== renderVersion) return;
      hero.classList.add('bp-hero-fading');
      settleTimer = delay(() => {
        if (version !== renderVersion) return;
        hero.style.setProperty('--bp-hero-cur', background);
        hero.classList.remove('bp-hero-fading');
        settleTimer = null;
      }, 640);
    };
    if (replacingActiveFade) {
      requestFrame(() => {
        if (version !== renderVersion) return;
        requestFrame(startFade);
      });
    } else {
      requestFrame(startFade);
    }
  };
}

function createDropdown(element) {
  const parentRow = element.parentElement;
  const row = element.matches?.('.bp-hsel-row')
    ? element
    : (parentRow?.matches?.('.bp-hsel-row') ? parentRow : element);
  const trigger = element.querySelector('[data-bp-trigger]')
    ?? row.querySelector('[data-bp-trigger]');
  const value = trigger?.querySelector('[data-bp-value]')
    ?? element.querySelector('[data-bp-value]')
    ?? row.querySelector('[data-bp-value]');
  const panel = element.querySelector('[data-bp-panel]')
    ?? row.querySelector('[data-bp-panel]');
  const label = row.querySelector('.bp-hsel-label');
  if (!label || !trigger || !value || !panel) return null;

  const options = Array.from(panel.querySelectorAll(OPTION_SELECTOR));
  let selectedOption = null;
  for (const option of options) {
    if (!selectedOption && option.getAttribute('aria-selected') === 'true') {
      selectedOption = option;
    }
  }

  return {
    activeOption: null,
    element,
    key: element.getAttribute('data-bp-dd'),
    label,
    options,
    panel,
    row,
    selectedOption,
    trigger,
    typeBuffer: '',
    typeTimer: null,
    value,
  };
}

function firstVisibleOption(dropdown) {
  for (const option of dropdown.options) {
    if (!option.hasAttribute('hidden')) return option;
  }
  return null;
}

function adjacentVisibleOption(dropdown, direction) {
  const options = dropdown.options;
  const activeIndex = options.indexOf(dropdown.activeOption);
  if (activeIndex < 0) return firstVisibleOption(dropdown);

  if (direction > 0) {
    for (let index = activeIndex + 1; index < options.length; index += 1) {
      if (!options[index].hasAttribute('hidden')) return options[index];
    }
  } else {
    for (let index = activeIndex - 1; index >= 0; index -= 1) {
      if (!options[index].hasAttribute('hidden')) return options[index];
    }
  }
  return dropdown.activeOption;
}

function prefixMatch(dropdown, query) {
  if (!query) return null;
  const normalizedQuery = query.toLowerCase();
  for (const option of dropdown.options) {
    if (
      !option.hasAttribute('hidden')
      && optionLabel(option).toLowerCase().startsWith(normalizedQuery)
    ) return option;
  }
  return null;
}

function isUnmodifiedPrintableKey(event) {
  return event.key?.length === 1
    && !event.ctrlKey
    && !event.metaKey
    && !event.altKey;
}

function findSelectorRoot(root) {
  if (root.matches?.(SELECTOR_ROOT)) return root;
  return root.querySelector?.(SELECTOR_ROOT) ?? null;
}

function stableElementId(element, kind, documentObject, claimedIds) {
  const existingId = trimmed(element.getAttribute('id'));
  const existingOwner = existingId ? documentObject?.getElementById?.(existingId) : null;
  if (
    existingId
    && !claimedIds.has(existingId)
    && (!existingOwner || existingOwner === element)
  ) {
    claimedIds.add(existingId);
    return existingId;
  }

  let id;
  do {
    generatedElementId += 1;
    id = `bp-hero-${kind}-${generatedElementId}`;
  } while (claimedIds.has(id) || documentObject?.getElementById?.(id));
  element.setAttribute('id', id);
  claimedIds.add(id);
  return id;
}

function configureDropdownAccessibility(dropdown, documentObject, claimedIds) {
  const labelId = stableElementId(dropdown.label, 'label', documentObject, claimedIds);
  const valueId = stableElementId(dropdown.value, 'value', documentObject, claimedIds);
  const triggerId = stableElementId(dropdown.trigger, 'combobox', documentObject, claimedIds);
  const panelId = stableElementId(dropdown.panel, 'listbox', documentObject, claimedIds);
  dropdown.trigger.setAttribute('role', 'combobox');
  dropdown.trigger.setAttribute('aria-haspopup', 'listbox');
  dropdown.trigger.setAttribute('aria-controls', panelId);
  dropdown.trigger.setAttribute('aria-labelledby', `${labelId} ${valueId}`);
  dropdown.trigger.setAttribute('aria-expanded', 'false');
  dropdown.trigger.removeAttribute('aria-activedescendant');
  dropdown.panel.setAttribute('role', 'listbox');
  dropdown.panel.setAttribute('aria-labelledby', triggerId);

  for (const option of dropdown.options) {
    stableElementId(option, 'option', documentObject, claimedIds);
    option.setAttribute('role', 'option');
    if (!option.hasAttribute('aria-selected')) option.setAttribute('aria-selected', 'false');
  }
}

export function initializeHeroSelector(root, environment = globalThis) {
  if (
    root?.getAttribute?.('data-bp-page') !== 'home'
    || root.getAttribute('data-bp-hero-runtime') !== 'v2'
  ) return 0;

  const selector = findSelectorRoot(root);
  if (!selector || selector.getAttribute(READY_ATTRIBUTE) === 'true') return 0;

  const dropdowns = [];
  let marketDropdown = null;
  let journeyDropdown = null;
  for (const element of selector.querySelectorAll('[data-bp-dd]')) {
    const dropdown = createDropdown(element);
    if (!dropdown) continue;
    dropdowns.push(dropdown);
    if (dropdown.key === 'market' && !marketDropdown) marketDropdown = dropdown;
    if (dropdown.key === 'journey' && !journeyDropdown) journeyDropdown = dropdown;
  }
  if (!marketDropdown || !journeyDropdown) return 0;

  const env = environment ?? globalThis;
  const documentObject = env.document ?? root.ownerDocument ?? globalThis.document;
  const windowObject = env.window ?? globalThis;
  const delay = env.setTimeout?.bind(env)
    ?? windowObject.setTimeout?.bind(windowObject)
    ?? ((callback) => callback());
  const cancelDelay = env.clearTimeout?.bind(env)
    ?? windowObject.clearTimeout?.bind(windowObject)
    ?? (() => {});
  const claimedIds = new Set();
  for (const dropdown of dropdowns) {
    configureDropdownAccessibility(dropdown, documentObject, claimedIds);
  }

  const hero = root.querySelector('.hn-hero');
  const featureHeading = root.querySelector('.hn-feat .hn-spec-h2');
  const featureCtaLabel = root.querySelector('.hn-hdr-cta.is-feat .bp-btn div')
    ?? root.querySelector('.hn-feat .hn-btn-txt');
  const cta = selector.querySelector('[data-bp-cta]') ?? root.querySelector('[data-bp-cta]');
  const headingTemplateNode = root.querySelector('[data-bp-feature-heading-template]');
  const ctaTemplateNode = root.querySelector('[data-bp-feature-cta-template]')
    ?? root.querySelector('[data-bp-feature-cta-label-template]');
  const marketUrlTemplateNode = root.querySelector('[data-bp-market-url-template]');
  const templates = {
    featureHeading: readHeadingElement(headingTemplateNode),
    featureCtaLabel: trimmed(ctaTemplateNode?.textContent),
    marketUrl: trimmed(marketUrlTemplateNode?.textContent),
    heroImageOverrides: collectHeroImageOverrides(root),
  };
  const defaultHeadingHtml = featureHeading?.innerHTML ?? '';
  const defaultFeatureCtaLabel = featureCtaLabel?.textContent ?? '';
  const applyHeroImage = createCrossfade(hero, env);
  let selectedMarket = null;

  const clearTypeahead = (dropdown) => {
    dropdown.typeBuffer = '';
    if (dropdown.typeTimer !== null) {
      cancelDelay(dropdown.typeTimer);
      dropdown.typeTimer = null;
    }
  };


  const setActive = (dropdown, option) => {
    for (const candidate of dropdown.options) candidate.classList.remove('is-active');
    dropdown.activeOption = option;
    if (!option) {
      dropdown.trigger.removeAttribute('aria-activedescendant');
      return;
    }
    option.classList.add('is-active');
    dropdown.trigger.setAttribute('aria-activedescendant', option.getAttribute('id'));
    option.scrollIntoView?.({ block: 'nearest' });
  };

  const updateTypeahead = (dropdown, key) => {
    dropdown.typeBuffer += key;
    const match = prefixMatch(dropdown, dropdown.typeBuffer);
    if (match) setActive(dropdown, match);
    if (dropdown.typeTimer !== null) cancelDelay(dropdown.typeTimer);
    dropdown.typeTimer = delay(() => {
      dropdown.typeBuffer = '';
      dropdown.typeTimer = null;
    }, 1500);
  };

  const close = (dropdown) => {
    dropdown.panel.classList.remove('is-open');
    dropdown.trigger.setAttribute('aria-expanded', 'false');
    clearTypeahead(dropdown);
    setActive(dropdown, null);
  };

  const closeAll = (except = null) => {
    for (const dropdown of dropdowns) {
      if (dropdown !== except) close(dropdown);
    }
  };

  const open = (dropdown) => {
    closeAll(dropdown);
    dropdown.panel.classList.add('is-open');
    dropdown.trigger.setAttribute('aria-expanded', 'true');
    const selected = dropdown.selectedOption;
    setActive(
      dropdown,
      selected && !selected.hasAttribute('hidden') ? selected : firstVisibleOption(dropdown),
    );
  };

  const setCtaDestination = (marketUrl) => {
    if (!cta) return;
    if (marketUrl) {
      cta.setAttribute('href', marketUrl);
      cta.removeAttribute('role');
      cta.removeAttribute('tabindex');
      return;
    }
    cta.removeAttribute('href');
    cta.setAttribute('role', 'button');
    cta.setAttribute('tabindex', '0');
  };

  const renderMarket = (data) => {
    applyHeroImage(data.heroImage);
    if (featureHeading) {
      featureHeading.innerHTML = data.featureHeading
        ? safeHeadingHtml(data.featureHeading)
        : defaultHeadingHtml;
    }
    if (featureCtaLabel) {
      featureCtaLabel.textContent = data.featureCtaLabel || defaultFeatureCtaLabel;
    }
    setCtaDestination(data.marketUrl);
  };

  const select = (dropdown, option) => {
    for (const candidate of dropdown.options) {
      candidate.setAttribute('aria-selected', candidate === option ? 'true' : 'false');
    }
    dropdown.selectedOption = option;

    const data = readHeroOptionData(option, templates);
    if (dropdown === marketDropdown) {
      data.marketUrl = validatedMarketUrl(data.marketUrl, env, documentObject, windowObject);
      selectedMarket = data;
      renderMarket(data);
      dropdown.value.textContent = `${data.label} Market`;
    } else {
      dropdown.value.textContent = data.label;
    }
    close(dropdown);
    dropdown.trigger.focus();
  };

  for (const dropdown of dropdowns) {
    dropdown.trigger.addEventListener('click', () => {
      if (dropdown.panel.classList.contains('is-open')) close(dropdown);
      else open(dropdown);
    });

    dropdown.trigger.addEventListener('keydown', (event) => {
      const isOpen = dropdown.panel.classList.contains('is-open');
      if (!isOpen) {
        if (
          event.key === 'Enter'
          || event.key === ' '
          || event.key === 'ArrowDown'
          || event.key === 'ArrowUp'
        ) {
          event.preventDefault();
          open(dropdown);
        } else if (isUnmodifiedPrintableKey(event)) {
          event.preventDefault();
          open(dropdown);
          updateTypeahead(dropdown, event.key);
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        close(dropdown);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(dropdown, adjacentVisibleOption(dropdown, event.key === 'ArrowDown' ? 1 : -1));
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (dropdown.activeOption) select(dropdown, dropdown.activeOption);
      } else if (event.key === 'Tab') {
        close(dropdown);
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        dropdown.typeBuffer = dropdown.typeBuffer.slice(0, -1);
        const match = prefixMatch(dropdown, dropdown.typeBuffer);
        setActive(dropdown, match || firstVisibleOption(dropdown));
      } else if (isUnmodifiedPrintableKey(event)) {
        event.preventDefault();
        updateTypeahead(dropdown, event.key);
      }
    });

    dropdown.panel.addEventListener('click', (event) => {
      const option = event.target.closest?.(OPTION_SELECTOR);
      if (option && dropdown.panel.contains(option)) select(dropdown, option);
    });

    dropdown.panel.addEventListener('mousemove', (event) => {
      const option = event.target.closest?.(OPTION_SELECTOR);
      if (
        option
        && option !== dropdown.activeOption
        && dropdown.panel.contains(option)
      ) setActive(dropdown, option);
    });
  }

  // Capture phase: controls that stop their click bubbling (the rail arrows) must still count as an outside click.
  documentObject?.addEventListener('click', (event) => {
    if (!selector.contains(event.target)) closeAll();
  }, { capture: true });
  documentObject?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  const openMarketPicker = () => {
    open(marketDropdown);
    marketDropdown.trigger.focus();
  };

  if (cta) {
    setCtaDestination('');
    cta.addEventListener('click', (event) => {
      if (selectedMarket?.marketUrl) return;
      event.preventDefault();
      openMarketPicker();
    });
    cta.addEventListener('keydown', (event) => {
      if (
        selectedMarket?.marketUrl
        || (event.key !== 'Enter' && event.key !== ' ')
      ) return;
      event.preventDefault();
      openMarketPicker();
    });
  }

  selector.setAttribute(READY_ATTRIBUTE, 'true');
  return 1;
}
